import { useState } from "react";
import type { PaymentMethodConfig } from "@cliqsuite/db";
import { useAdminData, type CfgTab } from "../state/AdminDataContext";
import { ConfigModal } from "../components/ConfigModal";

const COLUMNS: Array<{ tab: CfgTab; title: string; add: string; icon: string; empty: string }> = [
  { tab: "qr", title: "QR codes", add: "Add QR code", icon: "qr_code_2", empty: "No QR codes yet." },
  { tab: "bank", title: "Bank accounts", add: "Add bank account", icon: "account_balance", empty: "No bank accounts yet." },
  { tab: "wallet", title: "Crypto wallets", add: "Add wallet", icon: "currency_bitcoin", empty: "No wallets yet." },
];

function describe(tab: CfgTab, it: PaymentMethodConfig): { title: string; sub: string } {
  if (tab === "bank") return { title: it.bank_name ?? it.name, sub: [it.account_number, it.account_name].filter(Boolean).join(", ") };
  if (tab === "wallet") {
    const addr = it.wallet_address ?? "";
    return { title: it.name, sub: [it.network, addr ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : null].filter(Boolean).join(", ") };
  }
  return { title: it.name, sub: it.account_name ?? "Scan to pay" };
}

export function MethodsPage() {
  const { paycfg, cfgToggle, cfgDelete, cfgSave } = useAdminData();
  const [modal, setModal] = useState<{ tab: CfgTab; id: string | "new" } | null>(null);
  const existing = modal && modal.id !== "new" ? (paycfg[modal.tab].find((x) => x.id === modal.id) ?? null) : null;

  return (
    <div className="o-grid3" style={{ alignItems: "start", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))" }}>
      {COLUMNS.map((col) => (
        <section key={col.tab} className="o-card" style={{ margin: 0 }}>
          <div className="o-card__top">
            <h2 style={{ flex: 1 }}>{col.title}</h2>
          </div>
          <button type="button" className="o-add-card" style={{ minHeight: 52, width: "100%", marginBottom: 6 }} onClick={() => setModal({ tab: col.tab, id: "new" })}>
            <span className="ms" aria-hidden="true">add</span>
            {col.add}
          </button>
          {paycfg[col.tab].length === 0 && <div className="o-muted-note">{col.empty}</div>}
          {paycfg[col.tab].map((it) => {
            const on = it.is_enabled !== false;
            const d = describe(col.tab, it);
            return (
              <div key={it.id} className="o-list-row" style={{ opacity: on ? 1 : 0.6 }}>
                <span className="o-ico" style={{ overflow: "hidden" }}>
                  {it.image_data ? <img src={it.image_data} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span className="ms" aria-hidden="true">{col.icon}</span>}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="o-row__title">{d.title}</div>
                  <div className="o-row__sub">{d.sub}</div>
                </div>
                <button type="button" role="switch" aria-checked={on} aria-label={on ? `Hide ${d.title}` : `Show ${d.title}`} className="o-toggle" onClick={() => cfgToggle(col.tab, it.id)}>
                  <span className="o-toggle__track" aria-hidden="true" />
                </button>
                <button type="button" className="o-icon-btn" aria-label={`Edit ${d.title}`} onClick={() => setModal({ tab: col.tab, id: it.id })}>
                  <span className="ms" aria-hidden="true">edit</span>
                </button>
              </div>
            );
          })}
        </section>
      ))}

      {modal && (
        <ConfigModal
          tab={modal.tab}
          existing={existing}
          onClose={() => setModal(null)}
          onSubmit={(item) => {
            cfgSave(modal.tab, modal.id === "new" ? null : modal.id, item);
            setModal(null);
          }}
          onDelete={existing ? () => {
            cfgDelete(modal.tab, existing.id);
            setModal(null);
          } : undefined}
        />
      )}
    </div>
  );
}
